import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/authContext';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import RedirectService from '../../services/RedirectService';
import PolicyEngine from '../../services/PolicyEngine';
import AccessMessage from './AccessMessage';
import LoginModal from './LoginModal';
import { getAuthUrl } from '../../utils/subdomainUtils';
// import { logger } from "../../utils/logger";
import { UserAttributes, AccessPolicy, AccessFallbackProps, Role, Permission } from '../../types/accessControl';
import { logger } from "../../utils/logger";
import { isRouteOfflineSafe, isK9OpsRoute } from '../../config/offlineRoutes';
import OfflineBanner from '../offline/OfflineBanner';
import OfflineUnavailable from '../offline/OfflineUnavailable';
import OfflineExpired from '../offline/OfflineExpired';
import { K9_OFFLINE_AUTH_V2_ENABLED } from '../../config/featureFlags';
import { APEX_K9_MARK } from '../../../shared/branding/apexK9';
import { attributesHavePlatformAdminRole } from '../../utils/roleHelpers';
interface ProtectedRouteProps {
    children: React.ReactNode;
    /** Whether authentication is required (default: true) */
    requireAuth?: boolean;
    /** Specific policies to evaluate (overrides route-based policies) */
    policies?: AccessPolicy[];
    /** Legacy: Allowed roles (will be converted to policies) */
    allowedRoles?: Role[];
    /** Legacy: Allowed permissions (will be converted to policies) */
    allowedPermissions?: Permission[];
    /** Legacy: Permission checking mode (will be converted to policies) */
    requireAllPermissions?: boolean;
    /** Custom fallback component for access denied */
    fallbackComponent?: React.ComponentType<AccessFallbackProps>;
    /** Whether to use route-based policies (default: true) */
    useRoutePolicies?: boolean;
    /** Whether this is a backoffice route requiring admin roles */
    requireBackofficeRole?: boolean;
    /** Minimum clearance level required for access */
    minClearanceLevel?: number;
    /** Whether to show detailed policy information in error messages */
    showPolicyDetails?: boolean;
    /** Custom correlation ID for audit tracking */
    correlationId?: string;
    /** Allow users who need to change their password to access this route */
    allowPasswordChange?: boolean;
}
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = true, policies, allowedRoles = [], allowedPermissions = [], requireAllPermissions = false, fallbackComponent: CustomFallback, useRoutePolicies = true, requireBackofficeRole = false, minClearanceLevel, showPolicyDetails = false, correlationId: providedCorrelationId, allowPasswordChange = false }) => {
    const { isAuthenticated, user, isLoading, isInitializing, updateUserAndSession, authMode } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [showLoginModal, setShowLoginModal] = useState(false);
    logger.debug('🛡️ ProtectedRoute: Component is rendering for path:', { "arg0": location.pathname });
    logger.auth('🛡️ ProtectedRoute: Auth state - isAuthenticated:', { "arg0": isAuthenticated, "arg1": 'user:', "arg2": user ? user.email : 'null', "arg3": 'isLoading:', "arg4": isLoading, "arg5": 'isInitializing:', "arg6": isInitializing });
    logger.auth('🛡️ ProtectedRoute: Full auth context:', { "arg0": { isAuthenticated, hasUser: !!user, isLoading, isInitializing } });
    // CRITICAL FIX: Add a grace period after login to prevent modal from reappearing
    // This prevents race conditions where components check auth before session cookie is ready
    // MUST be declared before any conditional returns (React hooks rule)
    const [loginGracePeriod, setLoginGracePeriod] = useState(false);
    const loginGracePeriodRef = useRef<NodeJS.Timeout | null>(null);
    // Generate consistent correlation ID for session tracking
    const sessionCorrelationId = useRef<string | null>(null);
    const correlationId = useMemo(() => {
        if (providedCorrelationId)
            return providedCorrelationId;
        if (!sessionCorrelationId.current) {
            sessionCorrelationId.current = `route_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        return sessionCorrelationId.current;
    }, [providedCorrelationId]);
    // Check if user is in impersonation mode
    const isImpersonation = (user as any)?.is_impersonation === true;
    // Convert user to UserAttributes format
    const userAttributes: UserAttributes | null = user ? {
        id: user.id?.toString() || 'unknown',
        email: user.email || 'unknown',
        role: (user.role as Role) || 'guest',
        additionalRoles: (user as any).additionalRoles as Role[],
        department: (user as any).department,
        clearanceLevel: (user as any).clearanceLevel,
        location: (user as any).location,
        isActive: user.status === 'active',
        lastLogin: (user as any).lastLogin,
        permissions: (user as any).permissions as Permission[],
        mfaEnabled: (user as any).two_factor_enabled || (user as any).passkey_enabled || false,
        // During impersonation, treat MFA as verified to bypass requirements
        mfaVerified: isImpersonation ? true : ((user as any).mfaVerified || false),
        hasStrongAuth: (user as any).two_factor_enabled || (user as any).passkey_enabled || false,
        resetPasswordStatus: (user as any).reset_password_status || false,
        metadata: {
            ...((user as any).metadata || {}),
            is_impersonation: isImpersonation,
            impersonated_by: (user as any).impersonated_by,
            admin_email: (user as any).admin_email
        }
    } : null;
    // Debug MFA status (development only)
    if (import.meta.env.MODE === 'development') {
        logger.debug('🛡️ ProtectedRoute MFA Debug:', { "arg0": {
                userEmail: user?.email,
                mfaEnabled: userAttributes?.mfaEnabled,
                mfaVerified: userAttributes?.mfaVerified,
                hasStrongAuth: userAttributes?.hasStrongAuth,
                location: location.pathname,
                rawUserData: {
                    passkey_enabled: (user as any)?.passkey_enabled,
                    two_factor_enabled: (user as any)?.two_factor_enabled,
                    mfaVerified: (user as any)?.mfaVerified
                }
            } });
    }
    // Check if user has backoffice admin role
    const hasBackofficeRole = useCallback((userAttrs: UserAttributes | null): boolean => {
        if (!userAttrs)
            return false;
        if (attributesHavePlatformAdminRole(userAttrs)) {
            return true;
        }
        // During impersonation, check the admin's role instead of the impersonated user's role
        if (isImpersonation && userAttrs.metadata?.is_impersonation) {
            const adminRole = (user as any)?.admin_role;
            const adminRoles = (user as any)?.admin_roles || [];
            const adminRolesList = adminRoles.length > 0 ? adminRoles : (adminRole ? [adminRole] : []);
            // Check for admin roles in the admin's role list
            const backofficeAdminRoles = ['admin', 'policy_admin', 'system_admin', 'super_admin'];
            const hasAdminRole = backofficeAdminRoles.includes(adminRole) ||
                adminRolesList.some((role: string) => backofficeAdminRoles.includes(role));
            if (import.meta.env.MODE === 'development') {
                logger.debug('🛡️ Impersonation mode - checking admin role for backoffice access:', { "arg0": {
                        adminRole,
                        adminRoles: adminRolesList,
                        hasAdminRole
                    } });
            }
            return hasAdminRole;
        }
        // Check for admin roles (both database roles and access control roles)
        const adminRoles = ['admin', 'policy_admin', 'system_admin', 'super_admin'];
        return adminRoles.includes(userAttrs.role) ||
            Boolean(userAttrs.additionalRoles && userAttrs.additionalRoles.some(role => adminRoles.includes(role)));
    }, [isImpersonation, user]);
    // Check clearance level
    const hasRequiredClearance = useCallback((userAttrs: UserAttributes | null, requiredLevel?: number): boolean => {
        if (!requiredLevel || !userAttrs)
            return true;
        return (userAttrs.clearanceLevel || 0) >= requiredLevel;
    }, []);
    // Check if user session is valid and not expired
    const isSessionValid = useCallback((userAttrs: UserAttributes | null): boolean => {
        if (!userAttrs)
            return false;
        // Check if user is active
        if (userAttrs.isActive === false)
            return false;
        // Check last login time (if available)
        if (userAttrs.lastLogin) {
            const lastLogin = new Date(userAttrs.lastLogin);
            const now = new Date();
            const hoursSinceLogin = (now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60);
            // Consider session expired after 24 hours of inactivity
            if (hoursSinceLogin > 24)
                return false;
        }
        return true;
    }, []);
    // Check MFA requirements for sensitive routes
    const requiresMFA = useCallback((userAttrs: UserAttributes | null, isBackoffice: boolean): boolean => {
        if (!userAttrs)
            return false;
        // Bypass MFA requirements during impersonation (admin is impersonating user)
        // Check impersonation from user attributes or from the user object directly
        const isInImpersonationMode = userAttrs.metadata?.is_impersonation === true || isImpersonation;
        if (isInImpersonationMode) {
            if (import.meta.env.MODE === 'development') {
                logger.debug('🛡️ Impersonation mode active, bypassing MFA requirements');
            }
            return false;
        }
        // If user has already verified MFA in this session (via 2FA or passkey), no additional requirement
        if (userAttrs.mfaVerified) {
            if (import.meta.env.MODE === 'development') {
                logger.debug('🛡️ MFA already verified, no additional requirement');
            }
            return false;
        }
        // Check role-based MFA requirements first
        const rolesRequiringMFA = ['admin', 'coordinator', 'super_admin', 'policy_admin', 'system_admin'];
        const userRoleRequiresMFA = rolesRequiringMFA.includes(userAttrs.role);
        // If user's role requires MFA but they don't have strong authentication set up, require setup
        if (userRoleRequiresMFA && !userAttrs.hasStrongAuth) {
            if (import.meta.env.MODE === 'development') {
                logger.auth('🛡️ Admin role without strong auth, require setup');
            }
            return true;
        }
        // For backoffice routes, check if MFA is required based on policy
        if (isBackoffice) {
            // Check if user has strong authentication (2FA or passkey) but hasn't verified it in this session
            // Only require verification if they actually have strong auth enabled
            if (userAttrs.hasStrongAuth && !userAttrs.mfaVerified) {
                if (import.meta.env.MODE === 'development') {
                    logger.auth('🛡️ Backoffice route: User has strong auth but not verified in this session', { "arg0": {
                            hasStrongAuth: userAttrs.hasStrongAuth,
                            mfaVerified: userAttrs.mfaVerified,
                            mfaEnabled: userAttrs.mfaEnabled
                        } });
                }
                return true;
            }
            // If user's role requires MFA but they don't have strong authentication, require setup
            if (userRoleRequiresMFA && !userAttrs.hasStrongAuth) {
                if (import.meta.env.MODE === 'development') {
                    logger.auth('🛡️ Backoffice admin role without strong auth, require setup');
                }
                return true;
            }
            // Note: Removed duplicate check - already checked above
            return false;
        }
        // For other sensitive routes, check if user has strong authentication but hasn't completed it recently
        // Only require verification if they actually have strong auth enabled AND haven't verified
        if (userAttrs.hasStrongAuth && !userAttrs.mfaVerified) {
            if (import.meta.env.MODE === 'development') {
                logger.auth('🛡️ Sensitive route: User has strong auth but not verified', { "arg0": {
                        hasStrongAuth: userAttrs.hasStrongAuth,
                        mfaVerified: userAttrs.mfaVerified,
                        mfaEnabled: userAttrs.mfaEnabled
                    } });
            }
            return true;
        }
        return false;
    }, []);
    // Grace period effect: If we just became authenticated, start a grace period
    // This prevents the login modal from reappearing immediately after login
    // MUST be before any conditional returns (React hooks rule)
    useEffect(() => {
        // If we just became authenticated, start a grace period
        if (isAuthenticated && user) {
            setLoginGracePeriod(true);
            // Clear any existing timeout
            if (loginGracePeriodRef.current) {
                clearTimeout(loginGracePeriodRef.current);
            }
            // Grace period: 2 seconds to allow session cookie to propagate
            loginGracePeriodRef.current = setTimeout(() => {
                setLoginGracePeriod(false);
            }, 2000);
        }
        return () => {
            if (loginGracePeriodRef.current) {
                clearTimeout(loginGracePeriodRef.current);
            }
        };
    }, [isAuthenticated, user]);
    // Show loading while checking authentication or during initialization
    if (isLoading || isInitializing) {
        return (<Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2
            }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Checking authentication...
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          If this takes too long, please refresh the page
        </Typography>
      </Box>);
    }
    // If authentication is not required, render children
    if (!requireAuth) {
        return <>{children}</>;
    }

    // ---------------------------------------------------------------------
    // Offline auth-mode gating (K9 PWA scope)
    //
    // We branch BEFORE the "show login modal" path because hitting the
    // login modal while offline produces a hung dialog that posts to a
    // network that doesn't exist. Routes outside the K9 ops scope keep
    // their existing behavior (require online + LoginModal flow).
    // ---------------------------------------------------------------------
    const currentPathname = location.pathname;
    if (K9_OFFLINE_AUTH_V2_ENABLED && authMode === 'offline') {
        if (requireBackofficeRole) {
            // Backoffice always needs a live server session.
            return (
                <OfflineUnavailable
                    detail="Backoffice features require an active network connection. Reconnect and try again."
                />
            );
        }
        if (isRouteOfflineSafe(currentPathname)) {
            return (
                <>
                    <OfflineBanner />
                    {children}
                </>
            );
        }
        if (isK9OpsRoute(currentPathname)) {
            return <OfflineUnavailable />;
        }
        // Snapshot user is still `isAuthenticated` for K9, but this route is
        // not offline-safe (e.g. `/dashboard`). Do not render children — APIs
        // and policy would mislead. Same UX as blocked K9 tiles.
        return (
            <OfflineUnavailable
                detail={`This area is not available in offline mode. Use the ${APEX_K9_MARK} quick menu for offline tools, or reconnect.`}
            />
        );
    }

    if (
        K9_OFFLINE_AUTH_V2_ENABLED &&
        authMode === 'none' &&
        typeof navigator !== 'undefined' &&
        navigator.onLine === false
    ) {
        // Offline with no usable snapshot. Don't show a login modal that
        // can't possibly succeed; show the expired/missing screen for
        // K9 routes; for other routes, fall through to the standard
        // login modal which the parent app will surface as needed.
        if (isK9OpsRoute(currentPathname)) {
            return <OfflineExpired reason="missing" />;
        }
    }

    // If not authenticated, show login modal
    // Only show auth required after initialization is complete
    // Respect grace period to prevent modal from reappearing immediately after login
    if ((!isAuthenticated || !user) && !isInitializing && !loginGracePeriod) {
        const currentPath = location.pathname + location.search + location.hash;
        logger.auth('🛡️ ProtectedRoute: User not authenticated, showing login modal', { "arg0": {
                isAuthenticated,
                hasUser: !!user,
                isLoading,
                isInitializing,
                currentPath
            } });
        logger.debug('==== STORING REDIRECT URL IN PROTECTED ROUTE ====');
        logger.auth('Storing current path for post-login redirect:', { "arg0": currentPath });
        // Store the current location for redirect after login using centralized service
        RedirectService.storeCurrentUrlAsRedirect(currentPath);
        logger.auth('Stored! Now showing login modal.');
        const handleLoginSuccess = (user: any) => {
            logger.auth('✅ Login successful in ProtectedRoute', { "arg0": user });
            // Update auth context with new user data
            updateUserAndSession(user, 'session-from-protected-route');
            // Component will re-render with new auth state
        };
        const handleLoginClose = () => {
            logger.auth('🚪 User closed login modal, redirecting to home');
            window.location.href = getAuthUrl('/');
        };
        return (<LoginModal open={true} onClose={handleLoginClose} onSuccess={handleLoginSuccess} title="Authentication Required" description="Please log in to access this page."/>);
    }
    // Check if session is still valid
    if (!isSessionValid(userAttributes)) {
        // Store the current location for redirect after re-authentication
        const currentPath = location.pathname + location.search + location.hash;
        RedirectService.storeCurrentUrlAsRedirect(currentPath);
        return (<AccessMessage title="Session Expired" message="Your session has expired. Please log in again to continue." details={[
                `Session expired or user account deactivated`,
                `Last login: ${userAttributes?.lastLogin ? new Date(userAttributes.lastLogin).toLocaleString() : 'Unknown'}`
            ]} actionLabel="Go to Login" onAction={() => window.location.href = getAuthUrl('/login')} type="auth_required"/>);
    }
    // Check if user needs to change temporary password (skip if allowPasswordChange is true)
    if (userAttributes?.resetPasswordStatus && !allowPasswordChange) {
        return (<AccessMessage title="Password Change Required" message="You are using a temporary password. Please change your password to continue." details={[
                "Temporary password detected",
                "Password change required for security"
            ]} actionLabel="Change Password" onAction={() => navigate('/change-password', { state: { from: location.pathname + location.search + location.hash } })} type="password_change_required"/>);
    }
    // Check MFA requirements (skip during impersonation)
    if (!isImpersonation && requiresMFA(userAttributes, requireBackofficeRole)) {
        // If user has strong authentication but hasn't verified it, redirect to verification
        if (userAttributes?.hasStrongAuth && !userAttributes?.mfaVerified) {
            return (<AccessMessage title="2FA Required" message="This area requires two-factor authentication. Please complete 2FA verification to continue." details={[
                    `2FA verification required for ${requireBackofficeRole ? 'backoffice access' : 'this sensitive area'}`,
                    `Strong authentication: ${userAttributes?.hasStrongAuth ? 'Yes' : 'No'}`
                ]} actionLabel="Complete 2FA" onAction={() => navigate('/mfa-verify', { state: { from: location.pathname + location.search + location.hash } })} type="mfa_required"/>);
        }
        // If user doesn't have strong authentication, redirect to setup
        if (!userAttributes?.hasStrongAuth) {
            return (<AccessMessage title="Strong Authentication Required" message="This area requires strong authentication. Please set up 2FA or passkey authentication to continue." details={[
                    `Strong authentication required for ${requireBackofficeRole ? 'backoffice access' : 'this sensitive area'}`,
                    `Current status: No strong authentication configured`,
                    `Your role (${userAttributes?.role}) requires 2FA setup`
                ]} actionLabel="Setup Authentication" onAction={() => navigate('/mfa-setup', { state: { from: location.pathname + location.search + location.hash } })} type="mfa_required"/>);
        }
    }
    // Check backoffice role requirement
    if (requireBackofficeRole && !hasBackofficeRole(userAttributes)) {
        return (<AccessMessage title="Backoffice Access Required" message="This area requires administrative privileges. Please contact your system administrator." details={[
                `Required: Backoffice admin role`,
                `Your role: ${userAttributes?.role || 'none'}`,
                `Additional roles: ${userAttributes?.additionalRoles?.join(', ') || 'none'}`
            ]} actionLabel="Go to Dashboard" onAction={() => window.location.href = getAuthUrl('/dashboard')} type="access_denied"/>);
    }
    // Check clearance level requirement
    if (!hasRequiredClearance(userAttributes, minClearanceLevel)) {
        return (<AccessMessage title="Insufficient Clearance Level" message={`This area requires clearance level ${minClearanceLevel} or higher.`} details={[
                `Required clearance: ${minClearanceLevel}`,
                `Your clearance: ${userAttributes?.clearanceLevel || 0}`
            ]} actionLabel="Go to Dashboard" onAction={() => window.location.href = getAuthUrl('/dashboard')} type="access_denied"/>);
    }
    // Evaluate access using the new PolicyEngine with error handling
    let accessResult;
    try {
        // At this point, userAttributes should not be null due to earlier checks
        if (!userAttributes) {
            throw new Error('User attributes not available for policy evaluation');
        }
        // Skip policy evaluation if useRoutePolicies is false and no specific policies provided
        if (!useRoutePolicies && !policies && allowedRoles.length === 0 && allowedPermissions.length === 0) {
            accessResult = {
                granted: true,
                reason: 'No policies to evaluate',
                evaluatedPolicies: [],
                userAttributes,
                timestamp: new Date().toISOString(),
                duration: 0
            };
        }
        else {
            accessResult = PolicyEngine.evaluate(userAttributes, location.pathname, location.pathname, // Use pathname as resource
            correlationId);
        }
    }
    catch (error) {
        logger.error('Policy evaluation failed:', error, {});
        return (<Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Policy Evaluation Error
          </Typography>
          <Typography variant="body2">
            An error occurred while checking your access permissions. Please try refreshing the page or contact support if the issue persists.
          </Typography>
          {import.meta.env.MODE === 'development' && (<Typography variant="caption" component="pre" sx={{ mt: 1, fontSize: '0.75rem' }}>
              {error instanceof Error ? error.message : String(error)}
            </Typography>)}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mr: 2 }}>
          Refresh Page
        </Button>
        <Button variant="outlined" onClick={() => window.location.href = getAuthUrl('/dashboard')}>
          Go to Dashboard
        </Button>
      </Box>);
    }
    // If access is denied, show appropriate message
    if (!accessResult.granted) {
        const fallbackProps: AccessFallbackProps = {
            result: accessResult,
            user: userAttributes,
            resource: location.pathname,
            navigate
        };
        // Use custom fallback component if provided
        if (CustomFallback) {
            return <CustomFallback {...fallbackProps}/>;
        }
        // Build details array based on showPolicyDetails flag
        const details = [
            `Resource: ${location.pathname}`,
            `Your role: ${userAttributes?.role || 'none'}`
        ];
        // Only show sensitive policy details if explicitly enabled
        if (showPolicyDetails) {
            details.push(`Evaluated policies: ${accessResult.evaluatedPolicies?.join(', ') || 'none'}`, ...(accessResult.failedConditions ? [
                `Failed conditions: ${accessResult.failedConditions.length}`
            ] : []));
        }
        else {
            // Show user-friendly message without exposing internal details
            details.push(`Contact your administrator if you believe this is an error`);
        }
        // Default access denied message
        return (<AccessMessage title="Access Denied" message={accessResult.reason || "You don't have permission to access this page."} details={details} actionLabel="Go to Dashboard" onAction={() => window.location.href = getAuthUrl('/dashboard')} type="access_denied"/>);
    }
    // User is authenticated and has proper permissions
    logger.debug('🛡️ ProtectedRoute: User authenticated, rendering children');
    return <>{children}</>;
};
export default ProtectedRoute;
